import React from 'react';
import { useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SectionContent,
  StyledList,
  EnhancedGoBackButton
} from '../../components';
import { UK_RECRUITMENT_POLICY } from '../../data';
import ROUTES from '../../config/routes';
import THEME from '../../constants/theme';
import {
  SECTION_SPACING,
  SUBSECTION_SPACING,
  ELEMENT_SPACING
} from '../../constants';
import SEO from '../../components/SEO';
import { seoData } from '../../data/seoData';

const UKRecruitmentPolicyPage = ({ setRefs }) => {
  const ukRecruitmentPolicyRef = useRef(null);
  const dataCollectionRef = useRef(null);
  const thirdPartyDataRef = useRef(null);
  const whyProcessRef = useRef(null);
  const processingInfoRef = useRef(null);
  const rightToObjectRef = useRef(null);
  const accessToDataRef = useRef(null);
  const internationalTransfersRef = useRef(null);
  const dataRetentionRef = useRef(null);
  const retentionInfoRef = useRef(null);
  const yourRightsRef = useRef(null);
  const yourRightsInfoRef = useRef(null);
  const questionsRef = useRef(null);

  const refs = useMemo(
    () => ({
      ukRecruitmentPolicy: ukRecruitmentPolicyRef,
      dataCollection: dataCollectionRef,
      thirdPartyData: thirdPartyDataRef,
      whyProcess: whyProcessRef,
      processingInfo: processingInfoRef,
      rightToObject: rightToObjectRef,
      accessToData: accessToDataRef,
      internationalTransfers: internationalTransfersRef,
      dataRetention: dataRetentionRef,
      retentionInfo: retentionInfoRef,
      yourRights: yourRightsRef,
      yourRightsInfo: yourRightsInfoRef,
      questions: questionsRef
    }),
    []
  );

  // Update global refs if setRefs function is provided
  React.useEffect(() => {
    if (setRefs && typeof setRefs === 'function') {
      setRefs(refs);
    }
  }, [refs, setRefs]);

  const [selectedCompany, setSelectedCompany] = useState('mqcapital');

  const handleCompanyChange = (event, newCompany) => {
    if (newCompany !== null) {
      setSelectedCompany(newCompany);
    }
  };

  const currentCompany = UK_RECRUITMENT_POLICY.COMPANY_DATA[selectedCompany];

  return (
    <>
      <SEO
        title={seoData.ukRecruitmentPolicy.title}
        content={seoData.ukRecruitmentPolicy.content}
        keywords={seoData.ukRecruitmentPolicy.keywords}
        canonical={seoData.ukRecruitmentPolicy.canonical}
        ogImage={seoData.ukRecruitmentPolicy.ogImage}
      />
      {/* Enhanced Back Button with Company Toggle */}}
      <EnhancedGoBackButton
        backTo={ROUTES.HOW_TO_JOIN.ROOT}
        backText='Back to How to Join'
        showCompanyToggle={true}
        selectedCompany={selectedCompany}
        handleCompanyChange={handleCompanyChange}
        companies={[
          { value: 'mqcapital', label: 'MarQ Capital' },
          { value: 'spinnaker', label: 'Spinnaker' }
        ]}
      />
      {/* UK Recruitment Policy Section */}
      <SectionContent
        id='ukRecruitmentPolicy'
        ref={refs.ukRecruitmentPolicy}
        title={UK_RECRUITMENT_POLICY.TITLE}
        titleVariant='TitleLarge'
        titleMargin={`0 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.LAST_UPDATED,
            marginBottom: ELEMENT_SPACING,
            fontStyle: 'italic'
          },
          {
            text: currentCompany.intro,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* What information do we collect? section */}
      <SectionContent
        id='dataCollection'
        ref={refs.dataCollection}
        title={UK_RECRUITMENT_POLICY.DATA_COLLECTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.DATA_COLLECTION.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={UK_RECRUITMENT_POLICY.DATA_COLLECTION.ITEMS}
        />
      </Box>
      <SectionContent
        id='thirdPartyData'
        ref={refs.thirdPartyData}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.DATA_COLLECTION.THIRD_PARTY_TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: SECTION_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={UK_RECRUITMENT_POLICY.DATA_COLLECTION.THIRD_PARTY_ITEMS}
        />
      </Box>
      {/* Why do we process your personal data? section */}
      <SectionContent
        id='whyProcess'
        ref={refs.whyProcess}
        title={UK_RECRUITMENT_POLICY.WHY_PROCESS_DATA.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.WHY_PROCESS_DATA.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          items={UK_RECRUITMENT_POLICY.WHY_PROCESS_DATA.ITEMS.map(item => ({
            title: item.title,
            text: item.text.replace(
              /\[COMPANY NAME]/g,
              currentCompany.companyName
            )
          }))}
        />
      </Box>
      <SectionContent
        id='processingInfo'
        ref={refs.processingInfo}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.WHY_PROCESS_DATA.ADDITIONAL_TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: UK_RECRUITMENT_POLICY.WHY_PROCESS_DATA.VOLUNTARY_TEXT,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Your right to object section */}
      <SectionContent
        id='rightToObject'
        ref={refs.rightToObject}
        title={UK_RECRUITMENT_POLICY.RIGHT_TO_OBJECT.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: currentCompany.rightToObject,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Who has access to your personal data? section */}
      <SectionContent
        id='accessToData'
        ref={refs.accessToData}
        title={UK_RECRUITMENT_POLICY.ACCESS_TO_DATA.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.ACCESS_TO_DATA.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={UK_RECRUITMENT_POLICY.ACCESS_TO_DATA.ITEMS}
        />
      </Box>
      <SectionContent
        id='internationalTransfers'
        ref={refs.internationalTransfers}
        initialSpacing={false}
        text={UK_RECRUITMENT_POLICY.ACCESS_TO_DATA.INTERNATIONAL_TEXT.map(
          (text, index, array) => ({
            text: text.replace(/\[COMPANY NAME]/g, currentCompany.companyName),
            marginBottom:
              index === array.length - 1 ? SECTION_SPACING : ELEMENT_SPACING,
            fontSize: '1.3rem'
          })
        )}
      />
      {/* How long do we retain your personal data? section */}
      <SectionContent
        id='dataRetention'
        ref={refs.dataRetention}
        title={UK_RECRUITMENT_POLICY.DATA_RETENTION.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: currentCompany.retentionProcedures,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: 'The criteria used to determine retention periods include:',
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={UK_RECRUITMENT_POLICY.DATA_RETENTION.ITEMS}
        />
      </Box>
      <SectionContent
        id='retentionInfo'
        ref={refs.retentionInfo}
        initialSpacing={false}
        linkMappings={[
          {
            word: currentCompany.privacyEmail,
            url: `mailto:${currentCompany.privacyEmail}`,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          },
          {
            word: currentCompany.recruitingEmail,
            url: `mailto:${currentCompany.recruitingEmail}`,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={[
          {
            text: `If you would like more information about our retention policies for your personal data, please email ${currentCompany.privacyEmail}. If you have submitted a CV but no longer wish to be considered for employment with us, please email ${currentCompany.recruitingEmail}.`,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: 'If your job application is accepted, the personal data collected during recruitment will be transferred into your employee file and retained during your employment. Retention policies for employee data are specified in a separate privacy notice for employees.',
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Your rights section */}
      <SectionContent
        id='yourRights'
        ref={refs.yourRights}
        title={UK_RECRUITMENT_POLICY.YOUR_RIGHTS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.YOUR_RIGHTS.INTRO,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      <Box
        sx={{
          width: '100%',
          marginBottom: ELEMENT_SPACING,
          paddingLeft: {
            xs: THEME.SPACING.MEDIUM,
            sm: THEME.SPACING.MEDIUM,
            md: THEME.SPACING.XL
          }
        }}
      >
        <StyledList
          variant='blue'
          items={UK_RECRUITMENT_POLICY.YOUR_RIGHTS.ITEMS}
        />
      </Box>
      <SectionContent
        id='yourRightsInfo'
        ref={refs.yourRightsInfo}
        initialSpacing={false}
        text={[
          {
            text: UK_RECRUITMENT_POLICY.YOUR_RIGHTS.ADDITIONAL_TEXT,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: currentCompany.complaintInfo,
            marginBottom: ELEMENT_SPACING,
            fontSize: '1.3rem'
          },
          {
            text: currentCompany.periodicReview,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Questions, requests or concerns section */}
      <SectionContent
        id='questions'
        ref={refs.questions}
        title={UK_RECRUITMENT_POLICY.QUESTIONS.TITLE}
        titleVariant='TitleMedium'
        titleMargin={`${SUBSECTION_SPACING} 0 ${ELEMENT_SPACING} 0`}
        initialSpacing={false}
        linkMappings={[
          {
            word: currentCompany.privacyEmail,
            url: `mailto:${currentCompany.privacyEmail}`,
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underlined',
            newTab: false
          }
        ]}
        text={[
          {
            text: `${UK_RECRUITMENT_POLICY.QUESTIONS.TEXT} ${currentCompany.privacyEmail}`,
            marginBottom: SECTION_SPACING,
            fontSize: '1.3rem'
          }
        ]}
      />
      {/* Footer note with separator line */}
      <Box
        sx={{
          borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY}`,
          paddingTop: ELEMENT_SPACING,
          paddingBottom: ELEMENT_SPACING,
          mt: 4,
          mb: 2
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontSize: '1rem',
            fontStyle: 'italic',
            color: THEME.COLORS.NEUTRAL_GRAY,
            lineHeight: 1.6,
            display: 'block'
          }}
        >
          {UK_RECRUITMENT_POLICY.FOOTER_NOTE}
        </Typography>
      </Box>
    </>
  );
};

export default UKRecruitmentPolicyPage;
